// V97.116 — Generation IA des ALTERNATIVES de repas (app-only).
//
// Contexte produit : les alternatives de repas (4-5 recettes substituables par
// slot, que la cliente swap dans l'app) sont desormais APP-ONLY. Elles ne sont
// plus ecrites dans le texte du plan ni dans le doc Word/PDF envoye a la
// cliente. On les genere separement, ici, et on les stocke dans
// consultation.editorial_overrides.meals pour que le mapper les attache aux
// repas app.
//
// Ce generateur produit UNIQUEMENT des TITRES d'alternatives (title + hint
// court). Les recettes detaillees (ingredients, preparation) restent generees
// par aiRecipeGenerator apres coup, via le meme mecanisme mealKey(slot, title).
//
// SECURITE CLINIQUE (fail-closed) : ce generateur porte EXACTEMENT le meme bloc
// securite que aiRecipeGenerator (allergies, TCA, grossesse, contre-indications,
// sensibilite culturelle, traitements). On REUTILISE buildRecipeSystemPrompt
// comme socle clinique + identite, et on lui ajoute les regles de format
// specifiques aux alternatives. Aucune regle clinique n'est reecrite ici :
// l'autorite clinique reste Anissa (source unique = _clinicalContext.fr).

// V97.0 : passe par services/anthropic.js (callClaude + safeParseJson).
// Aucun modele code en dur : on laisse le defaut du repo (DEFAULT_MODEL).
import { callClaude, safeParseJson } from "./anthropic";
// Reutilise le socle clinique + identite du generateur de recettes : meme bloc
// securite scelle, meme position de priorite. On ne duplique pas les regles.
import {
  buildRecipeProfileContextLines,
  buildRecipeSystemPrompt,
  sanitizeRecipeDirectives,
} from "./aiRecipeGenerator";
import { hasRiskProfileAssessment } from "./riskProfileFlags";
import { NO_SELF_MENTION_INSTRUCTION } from "./aiMentionGuard";

const ALTERNATIVES_FORMAT_BODY_FR = `----- TA TACHE (ALTERNATIVES DE REPAS) -----

Pour CHAQUE repas principal fourni dans la liste, propose 4 a 5 alternatives de
repas COMPLETES et autosuffisantes, adaptees au profil et au slot horaire.

TON : pratique, signature Anissa. Tutoiement ("tu", "ton", "tes"). Zero emoji.

REGLES :
- 4 a 5 alternatives par repas (jamais moins de 4, jamais plus de 5)
- Chaque alternative est un repas COMPLET (pas un ingredient seul)
- Coherentes avec le slot du repas principal (petit-dejeuner reste un
  petit-dejeuner, souper reste un souper)
- Coherentes avec les contraintes du profil (allergies, evictions, objectif) :
  ne JAMAIS proposer un aliment interdit ou contre-indique
- Respecte ABSOLUMENT les DIRECTIVES D'ANISSA fournies dans le message (si
  presentes) : tout aliment qu'Anissa signale comme refuse / non aime (ex.
  "n'aime pas les oeufs", "ne mange pas de porc") ne doit apparaitre dans
  AUCUNE alternative NI dans AUCUN hint. Un refus / une aversion signale par
  Anissa est aussi contraignant qu'une allergie ou une eviction.
- VARIETE REELLE (le point le plus important pour la cliente) :
  * Ne te contente PAS de decliner la MEME base que le repas d'origine. Si le
    repas principal est a base d'un aliment (ex. oeufs), ne propose PAS 4 plats
    d'oeufs : propose majoritairement d'AUTRES bases pour qu'elle ne mange pas
    la meme chose tous les jours (pour un petit-dejeuner : porridge, tartines
    completes, skyr/yaourt + fruits, pancakes proteines, pain proteine... ;
    pour un repas : varie proteines, feculents et legumes).
  * Varie aussi les alternatives ENTRE elles : pas 5 fois la meme base.
- Cette variete reste TOUJOURS subordonnee a la securite et a la coherence :
  on diversifie autant que le profil le permet, JAMAIS au prix d'un aliment
  interdit/contre-indique, ni d'une incoherence avec le slot ou l'objectif.
- Ne PAS reproduire a l'identique le titre du repas principal
- Chaque alternative = un titre court + un hint concret (aliments cles +
  portions en grammes ou unites familieres)

FORMAT DU TITRE :
- Court, appetissant, clair : "Porridge sarrasin & cannelle"
- PAS de numerotation, PAS de puce, PAS de markdown

FORMAT DU HINT :
- Aliments cles + portions concretes : "40g flocons, lait amande, 100g myrtilles"
- Reste applicable et realiste pour 1 personne

----- OUTPUT -----

UNIQUEMENT du JSON valide, sans texte avant/apres, sans markdown, sans backticks.

{
  "alternatives": {
    "[mealKey]": [
      { "title": "string", "hint": "string" },
      ...
    ],
    ...
  }
}

La cle de chaque entree DOIT etre EXACTEMENT le "key" du repas principal tel que
fourni dans le user message. Aucune autre cle ne doit etre presente. Chaque
tableau contient 4 a 5 objets { title, hint }.
`;

/**
 * Assemble le system prompt alternatives AVEC le bloc securite clinique.
 * On reutilise buildRecipeSystemPrompt (identite + bloc securite scelle) et on
 * remplace le corps "recettes" par le corps "alternatives". Le bloc securite
 * n'est donc PAS reecrit : meme source, meme fail-closed que les recettes.
 *
 * @param {object} form - fiche cliente (source du bloc securite)
 * @param {string} [locale] - 'fr' | 'en'
 * @returns {string}
 */
export function buildAlternativesSystemPrompt(form = {}, locale = "fr") {
  // buildRecipeSystemPrompt = [identite, bloc securite, corps recettes].
  // On coupe le corps recettes (dernier bloc) et on colle notre corps
  // alternatives a la place, en gardant identite + securite intacts.
  const recipePrompt = buildRecipeSystemPrompt(form, locale);
  const marker = "----- TES OBLIGATIONS -----";
  const idx = recipePrompt.indexOf(marker);
  const clinicalBase = idx >= 0 ? recipePrompt.slice(0, idx).trimEnd() : recipePrompt;
  return [clinicalBase, ALTERNATIVES_FORMAT_BODY_FR, NO_SELF_MENTION_INSTRUCTION].filter(Boolean).join("\n\n");
}

export function buildAlternativesUserMessage({ meals, form, locale = "fr", directives = "" }) {
  const lines = [];

  lines.push("=== CONTEXTE ALIMENTAIRE MINIMAL (sans identite) ===");
  const profileLines = buildRecipeProfileContextLines(form);
  lines.push(...(profileLines.length > 0 ? profileLines : ["Aucune contrainte alimentaire renseignee."]));
  lines.push("");

  const directive = sanitizeRecipeDirectives(directives, form);
  if (directive) {
    lines.push("=== DIRECTIVES D'ANISSA — À RESPECTER ABSOLUMENT ===");
    lines.push("Anissa a saisi ces consignes pour cette cliente. Elles peuvent inclure");
    lines.push("des aliments refuses / non aimes : ne les propose dans AUCUNE alternative");
    lines.push("ni AUCUN hint.");
    lines.push(directive);
    lines.push("");
  }

  lines.push("=== LOCALE ===");
  lines.push(locale === "en" ? "english" : "francais");
  lines.push("");

  lines.push("=== REPAS PRINCIPAUX (a decliner en alternatives) ===");
  for (const m of meals) {
    lines.push(`- key: ${m.key}`);
    lines.push(`  slot: ${m.slot} (${m.slot_label})`);
    lines.push(`  titre: ${m.title}`);
    lines.push("");
  }

  lines.push("=== TACHE ===");
  lines.push("Genere les alternatives en JSON strict selon le format specifie.");
  lines.push(`Le JSON doit contenir EXACTEMENT ${meals.length} cles dans "alternatives",`);
  lines.push("une par repas principal, avec EXACTEMENT le 'key' fourni ci-dessus,");
  lines.push("chacune valant un tableau de 4 a 5 objets { title, hint }.");

  return lines.join("\n");
}

// Un repas principal genere 4-5 alternatives courtes (title + hint). ~150-220
// tokens par repas. Batch de 4 repas reste tres en dessous de maxTokens.
const BATCH_SIZE = 4;

async function callIaForBatch(meals, { form, locale, directives }) {
  const userMessage = buildAlternativesUserMessage({ meals, form, locale, directives });
  const rawText = await callClaude({
    system: buildAlternativesSystemPrompt(form, locale),
    user: userMessage,
    // Marge anti-troncature (les alternatives sont courtes mais on garde une
    // large marge de securite pour ne jamais couper un JSON).
    maxTokens: 8000,
  });
  const parsed = safeParseJson(rawText);

  const alternatives = parsed.alternatives || {};
  if (!alternatives || typeof alternatives !== "object" || Array.isArray(alternatives)) {
    throw new Error("Réponse invalide : alternatives absentes ou mal structurées.");
  }
  const expectedKeys = meals.map((meal) => meal.key).sort();
  const receivedKeys = Object.keys(alternatives).sort();
  if (JSON.stringify(expectedKeys) !== JSON.stringify(receivedKeys)) {
    throw new Error("Réponse invalide : les menus ne correspondent pas exactement aux repas demandés.");
  }
  for (const key of expectedKeys) {
    const options = alternatives[key];
    if (!Array.isArray(options) || options.length < 4 || options.length > 5
        || options.some((option) => !String(option?.title || "").trim())) {
      throw new Error(`Réponse invalide : menus incomplets pour ${key}. À valider Anissa.`);
    }
  }
  return alternatives;
}

/**
 * Genere des alternatives de repas pour une liste de repas principaux.
 *
 * @param {Array<{ key, slot, slot_label, title }>} meals - repas principaux
 * @param {object} ctx - { form, locale }
 * @returns {Promise<Array<{ slot, title, alternatives: Array<{ title, hint? }> }>>}
 *   Structure prete a merger dans consultation.editorial_overrides.meals.
 */
export async function generateAlternativesForMeals(meals, { form = {}, locale = "fr", directives = "" } = {}) {
  if (!Array.isArray(meals) || meals.length === 0) {
    throw new Error("Aucun repas principal a decliner.");
  }
  if (!hasRiskProfileAssessment(form) || buildRecipeProfileContextLines(form).length === 0) {
    throw new Error("Profil et allergies a completer puis confirmer dans Profil.");
  }

  // Decoupe en batches, tous en parallele.
  const batches = [];
  for (let i = 0; i < meals.length; i += BATCH_SIZE) {
    batches.push(meals.slice(i, i + BATCH_SIZE));
  }

  const settled = await Promise.allSettled(
    batches.map((batch) => callIaForBatch(batch, { form, locale, directives })),
  );

  const allAlts = {};
  const failures = [];
  settled.forEach((res, i) => {
    if (res.status === "fulfilled") {
      Object.assign(allAlts, res.value);
    } else {
      failures.push(res.reason);
      console.error(`[aiAlternativesGenerator] lot ${i + 1}/${batches.length} echoue`, res.reason);
    }
  });

  // Aucun resultat partiel : tous les lots doivent etre complets avant revue.
  if (failures.length > 0) {
    const firstMsg = failures[0]?.message || String(failures[0]);
    throw new Error(
      `Echec de la generation des alternatives : ${failures.length}/${batches.length} lots en echec. Aucun brouillon conserve. Detail : ${firstMsg}`,
    );
  }

  // Normalise en structure editorial_overrides.meals : [{ slot, title, alternatives }].
  const out = [];
  for (const meal of meals) {
    const raw = allAlts[meal.key];
    if (!Array.isArray(raw)) continue;
    const alternatives = raw
      .map((a) => {
        const title = typeof a?.title === "string" ? a.title.trim() : "";
        if (!title || title.length < 3) return null;
        const hint = typeof a?.hint === "string" ? a.hint.trim() : "";
        return hint ? { title, hint } : { title };
      })
      .filter(Boolean)
      .slice(0, 5);
    if (alternatives.length < 4) {
      throw new Error(`Menus incomplets pour ${meal.slot}. A valider Anissa.`);
    }
    out.push({ slot: meal.slot, title: meal.title, alternatives });
  }

  return out;
}
